import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { catchError, Observable, Subject, throwError } from 'rxjs';

import {
  PublicationRepository,
  PUBLICATION_REPOSITORY
} from '@app/domain/repositories/multimedia/publication/publication.repository.contract';
import { ErrorMsgEnum } from '@app/entities/enums/error/error-msg-enum';
import { IPublicationRequestParams } from '@app/entities/interfaces/multimedia/publication.request.interface';
import { ErrorCodes } from '@app/entities/models/error/error.model';
import { MultimediaMetadata } from '@app/entities/models/multimedia/multimedia-metadata.model';
import { AbstractPaginatedMultimediaItemResponse } from '@app/entities/models/multimedia/multimedia.abstract.model';
import { PublicationFeatured } from '@app/entities/models/multimedia/publication-featured.model';
import { Publication } from '@app/entities/models/multimedia/publication.model';

import { PublicationUseCases } from './publication.use-cases.contract';

@Injectable({
  providedIn: 'root'
})
export class PublicationUseCasesImpl implements PublicationUseCases {
  #publicationRepository: PublicationRepository = inject(PUBLICATION_REPOSITORY);
  #errorSubject: Subject<ErrorMsgEnum> = new Subject<ErrorMsgEnum>();
  error$ = this.#errorSubject.asObservable();

  getPaginatedItems(params: IPublicationRequestParams): Observable<AbstractPaginatedMultimediaItemResponse> {
    return this.#publicationRepository.getPaginatedItems(params).pipe(catchError(this.handleError));
  }

  getItemWithMetadata(translationKey: string, params: object): Observable<MultimediaMetadata<Publication>> {
    return this.#publicationRepository.getItemWithMetadata(translationKey, params).pipe(catchError(this.handleError));
  }

  getItem(translationKey: string, params: object): Observable<Publication> {
    return this.#publicationRepository.getItem(translationKey, params).pipe(catchError(this.handleError));
  }

  getPublicItem(slug: string, params: object): Observable<Publication> {
    return this.#publicationRepository.getPublicItem(slug, params).pipe(catchError(this.handleError));
  }

  updateItemVisualizations(translationKey: string): Observable<number> {
    return this.#publicationRepository.updateItemVisualizations(translationKey).pipe(catchError(this.handleError));
  }

  getPublicationFeatured(): Observable<PublicationFeatured> {
    return this.#publicationRepository.getPublicationFeatured().pipe(catchError(this.handleError));
  }

  private handleError = (error: HttpErrorResponse): Observable<never> => {
    let errorType;

    switch (error.status) {
      case ErrorCodes.LOCKED:
        errorType = ErrorMsgEnum.SERVICE_LOCKED;
        break;
      case ErrorCodes.SECTION_MAINTENANCE:
        errorType = ErrorMsgEnum.SECTION_MAINTENANCE;
        break;
      default:
        errorType = ErrorMsgEnum.SERVER_ERROR;
        break;
    }

    this.#errorSubject.next(errorType);

    return throwError(() => error);
  };
}
